import type {
  EdgeDirection,
  EdgeId,
  EdgeLabels,
  NodeSn,
  Packet,
  PathLookupGraphPreset,
  ReportSeverity,
  TrafficScore,
} from '@ipfabric/utils';
import classNames from 'classnames';
import type { PathLookupPathInspectorSelectedPacketSource } from 'components/DeviceView/deviceViewTypes';
import { useLocale } from 'containers/App/LocaleContext';
import { type FC, memo } from 'react';
import { FormattedMessage } from 'react-intl';
import {
  PacketDetail,
  type PacketDetailMouseEvent,
} from '../PacketDetail/PacketDetail';

export interface PathInspectorPacketSelectEvent {
  nodeSn: NodeSn;
  packetId: EdgeId;
  packetSource: PathLookupPathInspectorSelectedPacketSource;
}

interface Props {
  animate: boolean;
  deviceViewEntityNodeSn: NodeSn;
  edgeDirection?: EdgeDirection;
  labels?: EdgeLabels;
  onPacketDetailHover: (event: PacketDetailMouseEvent) => void;
  onPacketDetailLeave: (event: PacketDetailMouseEvent) => void;
  onPathInspectorPacketSelect?: (event: PathInspectorPacketSelectEvent) => void;
  packet: Packet;
  packetId: EdgeId;
  packetIndex: number;
  pathLookupPreset: PathLookupGraphPreset;
  selectType: 'first' | 'second';
  severityInfo?: Record<ReportSeverity, number>;
  sourceLabel: string | null;
  targetLabel: string | null;
  trafficScore?: TrafficScore;
  type: PathLookupPathInspectorSelectedPacketSource;
}

const BarePathInspectorPacketDetail: FC<Props> = (props) => {
  const msg = useLocale();

  const handlePacketClick = () => {
    props.onPathInspectorPacketSelect?.({
      nodeSn: props.deviceViewEntityNodeSn,
      packetId: props.packetId,
      packetSource: props.type,
    });
  };

  return (
    <div
      className={classNames(
        'relative flex cursor-pointer flex-col',
        'path-inspector-packet-select',
        props.animate && 'path-inspector-packet-select--shadow',
      )}
      onClick={handlePacketClick}
    >
      {props.selectType === 'second' && (
        <div className='z-elevated sticky top-0 flex flex-1 flex-col justify-start'>
          <div
            className={classNames(
              'path-inspector-packet-select__border',
              (props.type === 'edge' || props.type === 'generatedPackets') &&
                'path-inspector-packet-select__border--forwarded',
              props.type === 'acceptedPackets' &&
                'path-inspector-packet-select__border--accepted',
              props.type === 'droppedPackets' &&
                'path-inspector-packet-select__border--dropped',
            )}
          />
        </div>
      )}

      <PacketDetail
        detailId={props.packetId}
        deviceViewElement='node'
        disableHover
        edgeDirection={props.edgeDirection}
        labels={props.labels}
        onPacketDetailHover={props.onPacketDetailHover}
        onPacketDetailLeave={props.onPacketDetailLeave}
        packet={props.packet}
        pathLookupPreset={props.pathLookupPreset}
        severityInfo={props.severityInfo}
        showAnimation={false}
        showEmptyPlaceholder={false}
        sourceLabel={props.sourceLabel}
        targetLabel={props.targetLabel}
        trafficScore={
          props.selectType === 'first' ? props.trafficScore : undefined
        }
        title={
          props.type === 'acceptedPackets' ||
          props.type === 'droppedPackets' ||
          props.type === 'generatedPackets' ? (
            <div
              className={classNames(
                props.type === 'generatedPackets' && 'pb-5',
              )}
            >
              <FormattedMessage
                id='deviceView.trafficScore.title'
                defaultMessage={
                  msg.deviceView.devicePathLookup.selectPacket.internalPackets[
                    props.type
                  ]
                }
                values={{ index: props.packetIndex + 1 }}
              />
            </div>
          ) : undefined
        }
      />
    </div>
  );
};

export const PathInspectorPacketDetail = memo(BarePathInspectorPacketDetail);
